import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
    LogOut, Search, FileText, Users, Mail, Phone, Building2, DollarSign, Calendar,
    Filter, X, Send, MessageSquare, UserCog, Sparkles, ArrowRight, Flame,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../components/ui/sheet";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { api, formatApiError, TOKEN_KEY } from "../lib/api";
import { profileCompleteness } from "../lib/profileCompleteness";
import { LeadEngagementBadge, LeadEngagementCard } from "../components/EmailEngagement";

const STATUSES = ["All", "New", "Contacted", "Appointment Set", "Sold", "Closed"];
const statusTone = {
    New: "bg-blue-100 text-blue-800 border border-blue-200",
    Contacted: "bg-amber-100 text-amber-800 border border-amber-200",
    "Appointment Set": "bg-purple-100 text-purple-800 border border-purple-200",
    Sold: "bg-emerald-100 text-emerald-800 border border-emerald-200",
    Closed: "bg-slate-200 text-slate-700 border border-slate-300",
};

export default function AgentDashboard() {
    const navigate = useNavigate();
    const [me, setMe] = useState(null);
    const [leads, setLeads] = useState([]);
    const [stats, setStats] = useState({ total: 0, new: 0, contacted: 0, appointment_set: 0, sold: 0, hot: 0 });
    const [status, setStatus] = useState("All");
    const [search, setSearch] = useState("");
    const [hotOnly, setHotOnly] = useState(false);
    const [loading, setLoading] = useState(true);
    const [selected, setSelected] = useState(null);
    const [updating, setUpdating] = useState(false);
    const [noteText, setNoteText] = useState("");

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [meR, leadsR, statsR] = await Promise.all([
                api.get("/agent/me"),
                api.get("/agent/leads", { params: {
                    status: status === "All" ? undefined : status,
                    search: search || undefined,
                    hot: hotOnly ? true : undefined,
                } }),
                api.get("/agent/stats"),
            ]);
            setMe(meR.data);
            setLeads(leadsR.data.leads);
            setStats(statsR.data);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setLoading(false);
        }
    }, [status, search, hotOnly]);

    useEffect(() => { load(); }, [load]);

    // Deep-link from email "View Full Lead" button: ?lead=ID
    const [searchParams, setSearchParams] = useSearchParams();
    useEffect(() => {
        const leadId = searchParams.get("lead");
        if (!leadId || loading) return;
        (async () => {
            try {
                const { data } = await api.get(`/agent/leads/${leadId}`);
                setSelected(data);
                setNoteText("");
                const next = new URLSearchParams(searchParams);
                next.delete("lead");
                setSearchParams(next, { replace: true });
            } catch (err) { toast.error(formatApiError(err)); }
        })();
    }, [searchParams, loading, setSearchParams]);

    const logout = () => {
        localStorage.removeItem(TOKEN_KEY);
        navigate("/admin/login", { replace: true });
    };

    const updateStatus = async (lead, newStatus) => {
        setUpdating(true);
        try {
            await api.patch(`/agent/leads/${lead.id}/status`, { status: newStatus });
            toast.success(`Marked as ${newStatus}`);
            setSelected({ ...lead, status: newStatus });
            await load();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setUpdating(false);
        }
    };

    const addNote = async () => {
        if (!noteText.trim()) return;
        try {
            const { data } = await api.post(`/agent/leads/${selected.id}/notes`, { text: noteText });
            const updated = { ...selected, notes: [...(selected.notes || []), data] };
            setSelected(updated);
            setNoteText("");
            await load();
            toast.success("Note added");
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    return (
        <div className="min-h-screen bg-slate-50" data-testid="agent-dashboard">
            <header className="sticky top-0 z-30 bg-white border-b border-slate-200">
                <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        {me?.photo_url ? (
                            <img src={me.photo_url} alt="" className="w-10 h-10 rounded-full object-cover border border-slate-200" />
                        ) : (
                            <div className="w-10 h-10 rounded-full bg-navy-900 text-white flex items-center justify-center font-semibold">{(me?.name || "?").slice(0,1)}</div>
                        )}
                        <div>
                            <div className="font-serif text-xl text-navy-900">My Leads</div>
                            <div className="text-sm text-slate-700">Welcome, {me?.name || "Agent"} {me?.slug && (
                                <a className="ml-2 text-navy-900 underline" href={`/agent/${me.slug}`} target="_blank" rel="noreferrer" data-testid="my-landing-link">View public page</a>
                            )}</div>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <Button variant="outline" onClick={() => navigate("/agent/me/profile")} data-testid="agent-edit-profile" className="rounded-md">
                            <UserCog className="w-4 h-4 mr-2" /> Edit Profile
                        </Button>
                        <Button variant="outline" onClick={logout} data-testid="agent-logout-btn" className="rounded-md">
                            <LogOut className="w-4 h-4 mr-2" /> Sign out
                        </Button>
                    </div>
                </div>
            </header>

            <div className="max-w-7xl mx-auto px-6 py-10">
                <ProfileCompletenessBanner agent={me} onEdit={() => navigate("/agent/me/profile")} />
                <div className="grid sm:grid-cols-2 lg:grid-cols-6 gap-4">
                    <StatCard
                        label="Total"
                        value={stats.total}
                        icon={Users}
                        active={status === "All" && !hotOnly}
                        onClick={() => { setStatus("All"); setHotOnly(false); scrollToTable(); }}
                        testid="agent-stat-total"
                    />
                    <StatCard
                        label="New"
                        value={stats.new}
                        icon={Mail}
                        accent="bg-blue-50 text-blue-700"
                        ringColor="ring-blue-100 border-blue-500"
                        active={status === "New" && !hotOnly}
                        onClick={() => { setStatus("New"); setHotOnly(false); scrollToTable(); }}
                        testid="agent-stat-new"
                    />
                    <StatCard
                        label="Contacted"
                        value={stats.contacted}
                        icon={Phone}
                        accent="bg-amber-50 text-amber-700"
                        ringColor="ring-amber-100 border-amber-500"
                        active={status === "Contacted" && !hotOnly}
                        onClick={() => { setStatus("Contacted"); setHotOnly(false); scrollToTable(); }}
                        testid="agent-stat-contacted"
                    />
                    <StatCard
                        label="Appt Set"
                        value={stats.appointment_set}
                        icon={Calendar}
                        accent="bg-purple-50 text-purple-700"
                        ringColor="ring-purple-100 border-purple-500"
                        active={status === "Appointment Set" && !hotOnly}
                        onClick={() => { setStatus("Appointment Set"); setHotOnly(false); scrollToTable(); }}
                        testid="agent-stat-appt"
                    />
                    <StatCard
                        label="Sold"
                        value={stats.sold}
                        icon={DollarSign}
                        accent="bg-emerald-50 text-emerald-700"
                        ringColor="ring-emerald-100 border-emerald-500"
                        active={status === "Sold" && !hotOnly}
                        onClick={() => { setStatus("Sold"); setHotOnly(false); scrollToTable(); }}
                        testid="agent-stat-sold"
                    />
                    <button
                        type="button"
                        onClick={() => { setHotOnly((v) => !v); scrollToTable(); }}
                        data-testid="agent-hot-stat"
                        className={`text-left bg-white border-2 rounded-2xl p-5 transition-all ${hotOnly ? "border-rose-500 ring-2 ring-rose-100 shadow-lg" : "border-slate-200 hover:border-rose-300"}`}
                    >
                        <div className="flex items-center justify-between">
                            <div className="uppercase tracking-[0.2em] text-[11px] font-semibold text-rose-700 flex items-center gap-1">
                                <Flame className="w-3 h-3" /> Hot
                            </div>
                            <div className="w-9 h-9 rounded-md flex items-center justify-center bg-gradient-to-br from-rose-500 to-amber-500 text-white shadow-md">
                                <Flame className="w-4 h-4" />
                            </div>
                        </div>
                        <div className="mt-4 font-serif text-3xl text-rose-700">{stats.hot || 0}</div>
                        <div className="mt-1 text-[11px] text-slate-700">{hotOnly ? "Filter active" : "Click to filter"}</div>
                    </button>
                </div>

                <div id="leads-table-anchor" className="mt-10 bg-white border border-slate-200 rounded-2xl p-4 sm:p-6">
                    <div className="flex flex-col md:flex-row gap-3 md:items-center md:justify-between">
                        <div className="flex items-center gap-3 flex-1">
                            <div className="relative flex-1 max-w-md">
                                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-600" />
                                <Input data-testid="agent-search-input" placeholder="Search name, email, phone..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10 h-11" />
                            </div>
                            <div className="hidden sm:flex items-center gap-2 text-slate-700"><Filter className="w-4 h-4" /></div>
                            <Select value={status} onValueChange={setStatus}>
                                <SelectTrigger className="w-44 h-11" data-testid="agent-filter-status"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                                </SelectContent>
                            </Select>
                            <button
                                type="button"
                                onClick={() => setHotOnly((v) => !v)}
                                data-testid="agent-hot-filter-pill"
                                className={`inline-flex items-center gap-1.5 h-11 px-4 rounded-md border text-sm font-semibold transition-all ${hotOnly ? "bg-rose-500 text-white border-rose-500 shadow-md" : "bg-white text-rose-700 border-rose-200 hover:bg-rose-50"}`}
                            >
                                <Flame className="w-4 h-4" /> Hot only{stats.hot > 0 ? ` · ${stats.hot}` : ""}
                            </button>
                        </div>
                        <div className="text-sm text-slate-700">{leads.length} result{leads.length === 1 ? "" : "s"}</div>
                    </div>

                    <div className="mt-6 overflow-x-auto">
                        <Table data-testid="agent-leads-table">
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Name</TableHead>
                                    <TableHead>Phone</TableHead>
                                    <TableHead>Email</TableHead>
                                    <TableHead>Type</TableHead>
                                    <TableHead>Value</TableHead>
                                    <TableHead>Date</TableHead>
                                    <TableHead>Status</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {loading && <TableRow><TableCell colSpan={7} className="text-center py-12 text-slate-700">Loading…</TableCell></TableRow>}
                                {!loading && leads.length === 0 && <TableRow><TableCell colSpan={7} className="text-center py-12 text-slate-700">No leads assigned to you yet.</TableCell></TableRow>}
                                {leads.map((l) => (
                                    <TableRow key={l.id} className="cursor-pointer hover:bg-slate-50" onClick={() => { setSelected(l); setNoteText(""); }} data-testid={`agent-lead-row-${l.id}`}>
                                        <TableCell className="font-medium text-navy-900">
                                            <div className="flex items-center gap-2">
                                                <span>{l.full_name}</span>
                                                <LeadEngagementBadge lead={l} />
                                            </div>
                                        </TableCell>
                                        <TableCell className="text-slate-700">{l.phone}</TableCell>
                                        <TableCell className="text-slate-700">{l.email}</TableCell>
                                        <TableCell className="text-slate-700">{l.annuity_type}</TableCell>
                                        <TableCell className="text-slate-700">{l.current_value ? `$${Number(l.current_value).toLocaleString()}` : "—"}</TableCell>
                                        <TableCell className="text-slate-700 text-sm whitespace-nowrap">{formatDate(l.timestamp)}</TableCell>
                                        <TableCell>
                                            <div className="inline-flex items-center gap-1.5">
                                                <Badge className={statusTone[l.status] || statusTone.New}>{l.status}</Badge>
                                                {l.auto_promoted_at && (
                                                    <span title={l.auto_promotion_reason || "Auto-promoted by email engagement"} className="text-[9px] font-bold uppercase tracking-wider text-rose-600 bg-rose-50 border border-rose-200 px-1 py-0.5 rounded" data-testid="auto-promoted-badge">auto</span>
                                                )}
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </div>
                </div>
            </div>

            <Sheet open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
                <SheetContent className="w-full sm:max-w-xl overflow-y-auto" data-testid="agent-lead-detail">
                    {selected && (
                        <>
                            <SheetHeader>
                                <SheetTitle className="font-serif text-2xl text-navy-900">{selected.full_name}</SheetTitle>
                                <div className="flex items-center gap-2 mt-2 flex-wrap">
                                    <Badge className={statusTone[selected.status] || statusTone.New}>{selected.status}</Badge>
                                    {selected.auto_promoted_at && (
                                        <span title={selected.auto_promotion_reason || "Auto-promoted by email engagement"} className="text-[9px] font-bold uppercase tracking-wider text-rose-600 bg-rose-50 border border-rose-200 px-1.5 py-0.5 rounded">auto</span>
                                    )}
                                    <span className="text-sm text-slate-700">· {formatDate(selected.timestamp)}</span>
                                </div>
                            </SheetHeader>
                            <div className="mt-6 space-y-2">
                                <Row icon={Mail} label="Email" value={selected.email} link={`mailto:${selected.email}`} />
                                <Row icon={Phone} label="Phone" value={selected.phone} link={`tel:${selected.phone}`} />
                                <Row icon={Building2} label="Company" value={selected.company || "—"} />
                                <Row icon={DollarSign} label="Current value" value={selected.current_value ? `$${Number(selected.current_value).toLocaleString()}` : "—"} />
                            </div>
                            <div className="mt-6 grid grid-cols-2 gap-3">
                                <Field label="Inquiry type" value={formatInquiryType(selected.inquiry_type)} />
                                <Field label="Annuity Type" value={selected.annuity_type || "—"} />
                                <Field label="Goal" value={selected.goal || "—"} />
                                <Field label="Income Rider" value={selected.income_rider || "—"} />
                                <Field label="Surrender" value={selected.surrender_charge_known || "—"} />
                                <Field label="State" value={selected.state || "—"} />
                                <Field label="ZIP" value={selected.zip_code || "—"} />
                                <Field label="Lead source" value={formatLeadSource(selected.lead_source, selected.lead_source_detail)} />
                            </div>

                            <AppointmentCard appointment={selected.appointment} />

                            {(selected.income_confidence || selected.ltc_concern || selected.currently_taking_income ||
                              selected.financial_situation_changed || selected.marital_status || selected.review_focus) && (
                                <div className="mt-8 border-t border-slate-200 pt-6" data-testid="agent-needs-section">
                                    <div className="uppercase tracking-[0.2em] text-[11px] font-semibold text-slate-700 mb-3">Retirement picture</div>
                                    <div className="grid grid-cols-2 gap-3">
                                        {selected.income_confidence && <Field label="Enough income" value={selected.income_confidence} />}
                                        {selected.ltc_concern && <Field label="LTC concern" value={selected.ltc_concern} />}
                                        {selected.currently_taking_income && <Field label="Taking income" value={selected.currently_taking_income} />}
                                        {selected.financial_situation_changed && <Field label="Situation changed" value={selected.financial_situation_changed} />}
                                        {selected.marital_status && <Field label="Married/partnered" value={selected.marital_status} />}
                                        {selected.marital_status === "Yes" && selected.spouse_has_annuity && <Field label="Spouse has annuity" value={selected.spouse_has_annuity} />}
                                    </div>
                                    {selected.review_focus && (
                                        <div className="mt-3 bg-amber-50 border border-amber-200 rounded-md px-4 py-3">
                                            <div className="text-[11px] uppercase tracking-[0.2em] text-amber-800 font-semibold">Wants us to look at</div>
                                            <div className="mt-1 text-slate-900 text-sm whitespace-pre-wrap">{selected.review_focus}</div>
                                        </div>
                                    )}
                                </div>
                            )}

                            <LeadEngagementCard lead={selected} />

                            <div className="mt-8 border-t border-slate-200 pt-6">
                                <div className="uppercase tracking-[0.2em] text-[11px] font-semibold text-slate-700 mb-3">Update Status</div>
                                <div className="grid grid-cols-2 gap-2">
                                    {STATUSES.filter((s) => s !== "All").map((s) => (
                                        <Button key={s} disabled={updating || selected.status === s} onClick={() => updateStatus(selected, s)} data-testid={`agent-status-btn-${s.replace(/\s+/g,"-")}`} className={selected.status === s ? "bg-navy-900 text-white" : "bg-white border border-slate-200 text-slate-700 hover:bg-slate-50"}>{s}</Button>
                                    ))}
                                </div>
                            </div>

                            <div className="mt-8 border-t border-slate-200 pt-6">
                                <div className="uppercase tracking-[0.2em] text-[11px] font-semibold text-slate-700 mb-3 flex items-center"><MessageSquare className="w-4 h-4 mr-1" />Notes</div>
                                <div className="space-y-3 max-h-56 overflow-y-auto">
                                    {(selected.notes || []).length === 0 && <div className="text-sm text-slate-700">No notes yet.</div>}
                                    {(selected.notes || []).map((n) => (
                                        <div key={n.id || n.at} className="bg-slate-50 border border-slate-200 rounded-md p-3 text-sm">
                                            <div className="text-slate-800 whitespace-pre-wrap">{n.text}</div>
                                            <div className="text-xs text-slate-700 mt-1">{n.by} · {formatDate(n.at)}</div>
                                        </div>
                                    ))}
                                </div>
                                <div className="mt-3 flex items-start gap-2">
                                    <Textarea rows={2} value={noteText} onChange={(e) => setNoteText(e.target.value)} placeholder="Add a note..." data-testid="agent-note-input" />
                                    <Button onClick={addNote} disabled={!noteText.trim()} className="bg-navy-900 text-white" data-testid="agent-note-submit">
                                        <Send className="w-4 h-4" />
                                    </Button>
                                </div>
                            </div>

                            {selected.file_url && (
                                <div className="mt-8 border-t border-slate-200 pt-6">
                                    <div className="uppercase tracking-[0.2em] text-[11px] font-semibold text-slate-700">Statement</div>
                                    <a
                                        data-testid="agent-download-file"
                                        className="mt-2 inline-flex items-center gap-2 text-navy-900 underline"
                                        href="#"
                                        onClick={async (e) => {
                                            e.preventDefault();
                                            try {
                                                const r = await api.get(`/agent/leads/${selected.id}/file`, { responseType: "blob" });
                                                const url = URL.createObjectURL(r.data);
                                                window.open(url, "_blank");
                                                setTimeout(() => URL.revokeObjectURL(url), 10000);
                                            } catch (err) { toast.error(formatApiError(err)); }
                                        }}
                                    >
                                        <FileText className="w-4 h-4" /> {selected.file_name || "View statement"}
                                    </a>
                                </div>
                            )}

                            <button onClick={() => setSelected(null)} className="absolute top-4 right-4 text-slate-600 hover:text-slate-700" data-testid="agent-close-sheet">
                                <X className="w-5 h-5" />
                            </button>
                        </>
                    )}
                </SheetContent>
            </Sheet>
        </div>
    );
}

function ProfileCompletenessBanner({ agent, onEdit }) {
    if (!agent) return null;
    const { percent, missing, completed } = profileCompleteness(agent);
    if (percent === 100) return null;
    const tone = percent >= 60
        ? { ring: "ring-amber-200", bar: "bg-amber-500", pill: "bg-amber-100 text-amber-800 border-amber-200", icon: "text-amber-600" }
        : { ring: "ring-rose-200", bar: "bg-rose-500", pill: "bg-rose-100 text-rose-800 border-rose-200", icon: "text-rose-600" };
    const top = missing.slice(0, 3).map((m) => m.label).join(" · ");
    return (
        <div
            className={`mb-6 bg-white border border-slate-200 rounded-2xl p-5 ring-1 ${tone.ring}`}
            data-testid="dashboard-completeness-banner"
        >
            <div className="flex items-start gap-4">
                <div className={`w-10 h-10 rounded-md bg-slate-50 flex items-center justify-center ${tone.icon}`}>
                    <Sparkles className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                        <div className="font-serif text-lg text-navy-900">Finish your public profile</div>
                        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${tone.pill}`} data-testid="dashboard-completeness-percent">
                            {percent}% complete
                        </span>
                    </div>
                    <div className="mt-1 text-sm text-slate-600">
                        Still missing: <span className="text-slate-800 font-medium">{top}</span>
                        {missing.length > 3 ? <span className="text-slate-700"> +{missing.length - 3} more</span> : null}
                        <span className="text-slate-600"> · {completed.length}/{completed.length + missing.length} done</span>
                    </div>
                    <div className="mt-3 h-1.5 w-full bg-slate-100 rounded-full overflow-hidden max-w-md">
                        <div className={`h-full ${tone.bar} transition-all`} style={{ width: `${percent}%` }} />
                    </div>
                </div>
                <Button onClick={onEdit} className="bg-navy-900 hover:bg-navy-800 text-white shrink-0 hidden sm:inline-flex" data-testid="dashboard-completeness-cta">
                    Complete profile <ArrowRight className="w-4 h-4 ml-1.5" />
                </Button>
            </div>
            <Button onClick={onEdit} className="bg-navy-900 hover:bg-navy-800 text-white mt-4 sm:hidden w-full" data-testid="dashboard-completeness-cta-mobile">
                Complete profile <ArrowRight className="w-4 h-4 ml-1.5" />
            </Button>
        </div>
    );
}

function StatCard({ label, value, icon: Icon, accent = "bg-slate-50 text-slate-700", active = false, onClick, ringColor = "ring-navy-100 border-navy-500", testid }) {
    const baseClass = "text-left bg-white border-2 rounded-2xl p-5 transition-all w-full";
    const stateClass = onClick
        ? (active
            ? `${ringColor} ring-2 shadow-lg`
            : "border-slate-200 hover:-translate-y-0.5 hover:shadow-md hover:border-slate-300")
        : "border-slate-200";
    const content = (
        <>
            <div className="flex items-center justify-between">
                <div className="uppercase tracking-[0.2em] text-[11px] font-semibold text-slate-700">{label}</div>
                <div className={`w-9 h-9 rounded-md flex items-center justify-center ${accent}`}><Icon className="w-4 h-4" /></div>
            </div>
            <div className="mt-4 font-serif text-3xl text-navy-900">{value}</div>
            {onClick && (
                <div className="mt-1 text-[11px] text-slate-700">{active ? "Filter active · click to clear" : "Click to filter"}</div>
            )}
        </>
    );
    if (onClick) {
        return (
            <button type="button" onClick={onClick} data-testid={testid} className={`${baseClass} ${stateClass}`}>
                {content}
            </button>
        );
    }
    return <div data-testid={testid} className={`${baseClass} ${stateClass}`}>{content}</div>;
}

function scrollToTable() {
    const el = document.getElementById("leads-table-anchor");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
}

function Row({ icon: Icon, label, value, link }) {
    const inner = <span className="text-slate-800">{value}</span>;
    return (
        <div className="flex items-start gap-3 py-2">
            <div className="w-8 h-8 rounded-md bg-slate-100 text-slate-600 flex items-center justify-center"><Icon className="w-4 h-4" /></div>
            <div className="flex-1">
                <div className="text-xs text-slate-700">{label}</div>
                <div className="text-base">{link ? <a href={link} className="hover:underline">{inner}</a> : inner}</div>
            </div>
        </div>
    );
}

function Field({ label, value }) {
    return (
        <div className="bg-slate-50 rounded-md px-4 py-3">
            <div className="text-[11px] uppercase tracking-[0.2em] text-slate-700 font-semibold">{label}</div>
            <div className="mt-1 text-slate-900 text-sm font-medium">{value}</div>
        </div>
    );
}

function AppointmentCard({ appointment }) {
    if (!appointment || !appointment.start_time) return null;
    const isCanceled = !!appointment.canceled_at;
    const tone = isCanceled ? "border-rose-200 bg-rose-50" : "border-emerald-200 bg-emerald-50";
    const label = isCanceled ? "Appointment canceled" : "Appointment booked";
    const titleColor = isCanceled ? "text-rose-900" : "text-emerald-900";
    return (
        <div className={`mt-6 border-2 rounded-xl p-5 ${tone}`} data-testid="lead-appointment-card">
            <div className={`text-[11px] uppercase tracking-[0.2em] font-bold ${titleColor} mb-3`}>{label}</div>
            <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                    <div className="text-[10px] uppercase tracking-wider text-slate-700 font-semibold">Start</div>
                    <div className="mt-0.5 text-slate-900 font-medium">{formatDate(appointment.start_time)}</div>
                </div>
                {appointment.end_time && (
                    <div>
                        <div className="text-[10px] uppercase tracking-wider text-slate-700 font-semibold">End</div>
                        <div className="mt-0.5 text-slate-900 font-medium">{formatDate(appointment.end_time)}</div>
                    </div>
                )}
                {isCanceled && appointment.cancellation_reason && (
                    <div className="col-span-2">
                        <div className="text-[10px] uppercase tracking-wider text-rose-700 font-semibold">Reason</div>
                        <div className="mt-0.5 text-rose-900">{appointment.cancellation_reason}</div>
                    </div>
                )}
            </div>
            {!isCanceled && (appointment.reschedule_url || appointment.cancel_url) && (
                <div className="mt-3 flex flex-wrap gap-3 text-xs">
                    {appointment.reschedule_url && (
                        <a href={appointment.reschedule_url} target="_blank" rel="noreferrer" className="text-emerald-800 hover:text-emerald-700 underline">
                            Reschedule
                        </a>
                    )}
                    {appointment.cancel_url && (
                        <a href={appointment.cancel_url} target="_blank" rel="noreferrer" className="text-rose-700 hover:text-rose-600 underline">
                            Cancel booking
                        </a>
                    )}
                </div>
            )}
        </div>
    );
}

function formatDate(iso) {
    if (!iso) return "—";
    try {
        const d = new Date(iso);
        return d.toLocaleString(undefined, { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
    } catch { return iso; }
}

const LEAD_SOURCE_LABELS = {
    "google": "Google search",
    "google-ads": "Google ad",
    "facebook": "Facebook / Instagram",
    "tiktok": "TikTok",
    "youtube": "YouTube",
    "referral": "Friend referral",
    "agent-page": "Agent landing page",
    "email": "Email / newsletter",
    "direct": "Direct visit",
    "other": "Other",
};

const INQUIRY_TYPE_LABELS = {
    "review-existing": "Review existing annuity",
    "first-purchase": "First-time purchase",
    "additional-purchase": "Adding another annuity",
    "ira-roth-conversion": "IRA → Roth conversion",
    "cd-replacement": "CD replacement",
    "just-questions": "General questions",
};

function formatInquiryType(t) {
    if (!t) return "—";
    return INQUIRY_TYPE_LABELS[t] || t;
}

function formatLeadSource(source, detail) {
    if (!source) return "—";
    const label = LEAD_SOURCE_LABELS[source] || source;
    return detail ? `${label} · ${detail}` : label;
}
